import { SyntheticEvent, useEffect, useState } from 'react';
import ReactPlayer from 'react-player';
import { useTranslation } from 'react-i18next';
import audioThumbnail from '@assets/images/realtime-preview.svg';

type Props = { url: string; active: boolean };

export const RealtimeMediaPreview = ({ url, active }: Props) => {
  const { t } = useTranslation();
  const [hls, setHls] = useState(/\.m3u8(?:[?#]|$)/i.test(url));
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [hasVideo, setHasVideo] = useState<boolean | null>(null);
  const detectTracks = (event: SyntheticEvent<HTMLVideoElement>) =>
    setHasVideo(event.currentTarget.videoWidth > 0);
  useEffect(() => {
    setFailed(false);
    setPlaying(false);
    setHasVideo(null);
    setHls(/\.m3u8(?:[?#]|$)/i.test(url));
    if (!url || !active || /\.m3u8(?:[?#]|$)/i.test(url)) return;
    const controller = new AbortController();
    // Preview detection is best effort. Browser CORS or codec limitations
    // must never reject a source that the ingestion service can transcribe.
    void fetch(url, {
      headers: { Range: 'bytes=0-511' },
      signal: controller.signal
    })
      .then(async response => {
        const reader = response.body?.getReader();
        try {
          const first = await reader?.read();
          const prefix = first?.value
            ? new TextDecoder().decode(first.value.subarray(0, 512))
            : '';
          if (
            !controller.signal.aborted &&
            (/mpegurl/i.test(response.headers.get('content-type') || '') ||
              prefix.trimStart().startsWith('#EXTM3U'))
          )
            setHls(true);
        } finally {
          await reader?.cancel();
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, [url, active]);
  return (
    <section className="realtime-preview" aria-label={t('live-media-preview')}>
      <div className="realtime-preview-screen">
        {url && active ? (
          <ReactPlayer
            key={`${url}:${hls}`}
            url={url}
            playing={playing}
            width="100%"
            height="100%"
            controls
            playsinline
            config={{
              file: {
                forceVideo: true,
                forceHLS: hls,
                hlsOptions: {
                  backBufferLength: 30,
                  maxBufferLength: 30,
                  maxMaxBufferLength: 60
                },
                attributes: {
                  poster: audioThumbnail,
                  preload: 'metadata',
                  onLoadedData: detectTracks,
                  onResize: detectTracks
                }
              }
            }}
            onError={() => setFailed(true)}
            onReady={player => {
              setFailed(false);
              const media = player.getInternalPlayer() as HTMLVideoElement;
              setHasVideo(media?.readyState >= 2 ? media.videoWidth > 0 : null);
            }}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
          />
        ) : (
          <img
            src={audioThumbnail}
            alt=""
            className="realtime-preview-poster"
          />
        )}
      </div>
      {url && active && (
        <div className="realtime-preview-controls">
          <button
            type="button"
            onClick={() => setPlaying(value => !value)}
            aria-label={t(playing ? 'pause' : 'play')}
          >
            {t(playing ? 'pause' : 'play')}
          </button>
          <span>
            {t(
              hasVideo === null
                ? 'live-media-preview'
                : hasVideo
                  ? 'video-streaming'
                  : 'audio-streaming'
            )}
          </span>
        </div>
      )}
      <div
        className="realtime-preview-caption"
        role={failed ? 'status' : undefined}
      >
        {t(
          failed
            ? 'live-preview-unavailable'
            : !url || !active
              ? 'live-preview-empty'
              : hasVideo === false
                ? 'live-preview-audio-help'
                : 'live-preview-playback-help'
        )}
      </div>
    </section>
  );
};
